import { useState, useEffect, useCallback } from 'react'
import { Check, Share2, X, Pencil, Plus, ArrowLeft } from 'lucide-react'

// Selection des annotations a partager — ANNOTATIONS-002.
//
// POURQUOI UNE MODALE, ET PAS LE PANNEAU CLIPS. La premiere version posait les
// cases a cocher dans le panneau des clips · replie par defaut, derriere une
// case de 22 px sans libelle, avec un CTA qui n'apparaissait qu'apres avoir
// coche. La fonction etait invisible tant qu'on ne l'avait pas deja trouvee —
// exactement `CHEMIN-NON-EMPRUNTE-001`, le defaut du lot 1, reproduit.
//
// Et surtout · ce panneau liste des CLIPS, et aucun clip de la base ne porte
// d'annotation. Y mettre la selection revenait a proposer de partager
// precisement ce qui n'a rien a montrer.
//
// Ici, un point d'entree unique dans le header ouvre une liste qui s'explique
// d'elle-meme.
//
// ON ROUVRE AUSSI (ANNOTATIONS-004). La modale liste les seances deja
// partagees sur ce match · le coach qui vient partager y trouve ce qu'il a
// deja partage. Rouvrir une seance garde LE MEME LIEN · celui qui est chez les
// joueurs reste valable, il n'y a rien a renvoyer.
//
// ON NOMME DES ACTIONS, PAS DES ANNOTATIONS (ANNOTATIONS-003). Trois
// annotations decrivant le meme but encaisse forment UNE action a trois arrets ·
// le coach veut un titre, pas trois. Les groupes viennent du SERVEUR, qui est
// seul a porter la regle de regroupement · la recalculer ici en serait une
// deuxieme definition, et ce module en a deja paye trois.

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

const D     = '#1a1916'
const CREAM = '#f5f2eb'
const BLUE  = '#4F8EF7'
const MUTED = 'rgba(26,25,22,0.5)'
const RULE  = 'rgba(26,25,22,0.12)'

const DISP = "'Barlow Condensed', sans-serif"
const BODY = "'Space Grotesk', sans-serif"
const MONO = "'JetBrains Mono', 'Space Grotesk', monospace"

const TYPE_LABEL = {
  goal: 'But', chance: 'Occasion', loss: 'Perte', recovery: 'Récup',
  transition_for: 'Transition off.', transition_against: 'Transition subie',
  defensive_phase: 'Défense', set_piece: 'CPA',
  tactical_note: 'Séquence', player_action: 'Geste',
}

function fmtClock(s) {
  if (!Number.isFinite(s) || s < 0) s = 0
  const total = Math.floor(s)
  const m = Math.floor(total / 60)
  return `${String(m).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

const TITRE_MAX = 60

function dateCourte(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

/**
 * @param matchId
 * @param onShare  (groupes, token) => void · token nul = nouvelle seance
 */
export default function PartageAnnotationsModal({ matchId, onShare, onClose, sharing = false }) {
  // 'liste' = ce qui a deja ete partage · 'composer' = une seance, neuve ou
  // rouverte. Le token distingue les deux : nul, on cree.
  const [vue, setVue] = useState('liste')
  const [token, setToken] = useState(null)
  const [seances, setSeances] = useState(null)
  const [groupes, setGroupes] = useState(null)
  const [erreur, setErreur] = useState(null)

  // Coches et titres, indexes sur le rang du groupe dans la liste chargee ·
  // etat d'ecran. Le titre voyage ensuite avec les `moment_ids` de son groupe,
  // jamais avec sa position.
  const [choisis, setChoisis] = useState([])
  const [titres, setTitres] = useState({})

  const charger = useCallback((url, prepare) => {
    setGroupes(null)
    setErreur(null)
    fetch(url, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('http')))
      .then(d => {
        const liste = d.groupes || []
        setGroupes(liste)
        prepare(liste)
      })
      .catch(() => setErreur("Les annotations n'ont pas pu être chargées."))
  }, [])

  useEffect(() => {
    fetch(`${API}/matches/${matchId}/clip-shares/`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('http')))
      .then(d => setSeances(d.seances || []))
      .catch(() => setSeances([]))
  }, [matchId])

  const composerNeuve = () => {
    setToken(null)
    setTitres({})
    setVue('composer')
    // Tout coche par defaut · le coach a ouvert cet ecran pour partager, pas
    // pour selectionner. Il decoche ce qu'il ne veut pas.
    charger(`${API}/matches/${matchId}/annotation-groups/`, l => setChoisis(l.map((_, i) => i)))
  }

  const rouvrir = (t) => {
    setToken(t)
    setVue('composer')
    // A la reouverture, seul ce qui EST DEJA dans la seance est coche · rien ne
    // part chez les joueurs sans que le coach l'ait decide.
    charger(`${API}/clip-shares/${t}/edit/`, (l) => {
      setChoisis(l.map((g, i) => (g.dans_la_seance ? i : null)).filter(i => i !== null))
      setTitres(Object.fromEntries(l.map((g, i) => [i, g.titre || ''])))
    })
  }

  const basculer = (i) => setChoisis(
    prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i])

  const retenus = (groupes || []).filter((_, i) => choisis.includes(i))
  const arrets = retenus.reduce((n, g) => n + g.stops.length, 0)

  const envoyer = () => {
    if (!retenus.length || sharing) return
    onShare((groupes || [])
      .map((g, i) => ({ groupe: g, titre: (titres[i] || '').trim() }))
      .filter((_, i) => choisis.includes(i))
      .map(({ groupe, titre }) => ({ moment_ids: groupe.moment_ids, titre: titre || null })),
      token)
  }

  const cadre = (contenu, bas) => (
    <div
      role="dialog"
      aria-label={vue === 'liste' ? 'Séances partagées' : 'Composer une séance'}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(26,25,22,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{
        background: CREAM, borderRadius: 10, maxWidth: 560, width: '100%',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        {contenu}
        {bas}
      </div>
    </div>
  )

  const entete = (titre, sousTitre, retour) => (
    <div style={{ padding: '20px 22px 14px', borderBottom: `1px solid ${RULE}` }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {retour && (
            <button onClick={retour} aria-label="Revenir aux séances"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, padding: '2px 0' }}>
              <ArrowLeft size={18} />
            </button>
          )}
          <div>
            <h2 style={{ fontFamily: DISP, fontSize: 22, textTransform: 'uppercase', color: D, margin: '0 0 6px', lineHeight: 1 }}>
              {titre}
            </h2>
            <p style={{ fontFamily: BODY, fontSize: 13, color: MUTED, margin: 0, lineHeight: 1.5 }}>
              {sousTitre}
            </p>
          </div>
        </div>
        <button onClick={onClose} aria-label="Fermer"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: MUTED, padding: 4 }}>
          <X size={18} />
        </button>
      </div>
    </div>
  )

  // ─── Vue liste ────────────────────────────────────────────────────────────
  if (vue === 'liste') {
    return cadre(
      <>
        {entete('Séances partagées',
          "Reprends une séance déjà envoyée, ou compose-en une nouvelle.")}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {seances === null && (
            <p style={{ fontFamily: BODY, fontSize: 13, color: MUTED, padding: '18px 22px', margin: 0 }}>Chargement…</p>
          )}
          {seances?.length === 0 && (
            <p style={{ fontFamily: BODY, fontSize: 13, color: MUTED, padding: '18px 22px', margin: 0, lineHeight: 1.5 }}>
              Aucune séance partagée sur ce match pour l'instant.
            </p>
          )}
          {seances?.map((s, i) => (
            <div key={s.token} style={{
              padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 10,
              borderBottom: i === seances.length - 1 ? 'none' : `1px solid ${RULE}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: BODY, fontSize: 13, color: D }}>
                  {s.titres.length ? s.titres.join(' · ') : `${s.groupes} action${s.groupes > 1 ? 's' : ''}`}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: MUTED, marginTop: 3 }}>
                  {s.token} · {s.expire ? 'expirée' : `expire le ${dateCourte(s.expires_at)}`}
                </div>
              </div>
              {/* Une seance expiree ne se rouvre pas · elle ne mene plus nulle
                  part, et la rouvrir donnerait le sentiment de l'avoir reparee. */}
              {s.modifiable && !s.expire && (
                <button
                  onClick={() => rouvrir(s.token)}
                  aria-label={`Modifier la séance ${s.token}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontFamily: BODY, fontSize: 12, minHeight: 36, padding: '0 12px',
                    background: 'transparent', color: D, cursor: 'pointer',
                    border: `1px solid ${RULE}`, borderRadius: 6,
                  }}
                >
                  <Pencil size={12} /> Modifier
                </button>
              )}
              {/* On montre les seances des autres coachs plutot que de les
                  masquer · les cacher laisserait croire qu'elles n'existent pas. */}
              {!s.modifiable && (
                <span style={{ fontFamily: MONO, fontSize: 9, color: MUTED, whiteSpace: 'nowrap' }}>
                  autre coach
                </span>
              )}
            </div>
          ))}
        </div>
      </>,
      <div style={{ padding: '12px 22px', borderTop: `1px solid ${RULE}`, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={composerNeuve}
          aria-label="Composer une nouvelle séance"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: BODY, fontSize: 13, fontWeight: 600,
            minHeight: 40, padding: '0 16px', borderRadius: 6,
            background: BLUE, color: '#fff', border: 'none', cursor: 'pointer',
          }}
        >
          <Plus size={14} /> Nouvelle séance
        </button>
      </div>,
    )
  }

  // ─── Vue composition (neuve ou rouverte) ──────────────────────────────────
  return cadre(
    <>
      {entete(
        token ? 'Modifier la séance' : 'Partager des annotations',
        token
          ? "Le lien ne change pas · tes joueurs verront la mise à jour en rechargeant."
          : "Nomme chaque action pour la retrouver dans la liste.",
        () => setVue('liste'),
      )}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {erreur && (
          <p style={{ fontFamily: BODY, fontSize: 13, color: D, padding: '18px 22px', margin: 0 }}>{erreur}</p>
        )}
        {!erreur && groupes === null && (
          <p style={{ fontFamily: BODY, fontSize: 13, color: MUTED, padding: '18px 22px', margin: 0 }}>Chargement…</p>
        )}
        {groupes?.map((g, i) => {
          const coche = choisis.includes(i)
          return (
            <div key={g.moment_ids.join('-')} style={{
              padding: '12px 22px',
              borderBottom: i === groupes.length - 1 ? 'none' : `1px solid ${RULE}`,
              background: coche ? 'rgba(79,142,247,0.06)' : 'transparent',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  onClick={() => basculer(i)}
                  aria-pressed={coche}
                  aria-label={`${coche ? 'Retirer' : 'Ajouter'} l'action de ${fmtClock(g.stops[0].t)}`}
                  style={{
                    width: 20, height: 20, borderRadius: 4, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: coche ? BLUE : 'transparent',
                    border: `1px solid ${coche ? BLUE : RULE}`, color: '#fff',
                    cursor: 'pointer', padding: 0,
                  }}
                >
                  {coche && <Check size={12} />}
                </button>
                <span style={{ fontFamily: MONO, fontSize: 11, color: BLUE, flexShrink: 0 }}>
                  {fmtClock(g.stops[0].t)}
                </span>
                <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED, flexShrink: 0 }}>
                  {g.stops.length} arrêt{g.stops.length > 1 ? 's' : ''}
                </span>
                {/* Ce qui n'est pas encore dans la seance est annonce comme tel ·
                    sinon le coach ne sait pas ce qu'il ajoute. */}
                {token && !g.dans_la_seance && (
                  <span style={{
                    fontFamily: MONO, fontSize: 9, letterSpacing: '.08em',
                    color: BLUE, border: `1px solid ${BLUE}55`, borderRadius: 3,
                    padding: '2px 5px', whiteSpace: 'nowrap',
                  }}>
                    nouvelle
                  </span>
                )}
              </div>
              <input
                value={titres[i] || ''}
                onChange={e => setTitres(t => ({ ...t, [i]: e.target.value.slice(0, TITRE_MAX) }))}
                disabled={!coche}
                maxLength={TITRE_MAX}
                placeholder={`${TYPE_LABEL[g.stops[0].moment_type] || 'Séquence'} · sans titre`}
                aria-label={`Titre de l'action de ${fmtClock(g.stops[0].t)}`}
                style={{
                  width: '100%', marginTop: 8, boxSizing: 'border-box',
                  fontFamily: BODY, fontSize: 13, padding: '8px 10px', minHeight: 38,
                  border: `1px solid ${RULE}`, borderRadius: 6,
                  background: coche ? '#fff' : 'transparent', color: D,
                  opacity: coche ? 1 : 0.5,
                }}
              />
            </div>
          )
        })}
      </div>
    </>,
    <div style={{
      padding: '12px 22px', borderTop: `1px solid ${RULE}`,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '.08em', color: MUTED, flex: 1 }}>
        {retenus.length} action{retenus.length > 1 ? 's' : ''} · {arrets} arrêt{arrets > 1 ? 's' : ''}
      </span>
      <button
        onClick={envoyer}
        disabled={!retenus.length || sharing}
        aria-label={token
          ? `Mettre à jour la séance (${retenus.length} action${retenus.length > 1 ? 's' : ''})`
          : `Générer le lien de séance (${retenus.length} action${retenus.length > 1 ? 's' : ''})`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontFamily: BODY, fontSize: 13, fontWeight: 600,
          minHeight: 40, padding: '0 16px', borderRadius: 6,
          background: BLUE, color: '#fff', border: 'none',
          cursor: (!retenus.length || sharing) ? 'not-allowed' : 'pointer',
          opacity: (!retenus.length || sharing) ? 0.45 : 1,
        }}
      >
        <Share2 size={13} /> {sharing ? 'Enregistrement…' : (token ? 'Mettre à jour' : 'Générer le lien')}
      </button>
    </div>,
  )
}
